import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams, useParams } from 'react-router-dom';
import { authService } from '../services/auth';
import { config } from '../config';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import Comments from '../components/comments/Comments';
import SEO from '../components/common/SEO';

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #cccccc;
  margin: 0;
`;



const Tabs = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
`;

const Tab = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: 10px 20px;
  font-size: 1rem;
  color: ${props => props.$active ? '#4CAF50' : '#666'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? '#4CAF50' : 'transparent'};
  transition: all 0.2s;

  &:hover {
    color: #4CAF50;
  }
`;





const ProfilePicture = styled.div<{ $imageUrl: string }>`
  width: 128px;
  height: 128px;
  border-radius: 50%;
  background-image: url(${props => props.$imageUrl});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 0px ;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;


`;

// Settings UI Components
const SettingsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SettingsSection = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 30px;
  margin-bottom: 20px;
`;


// Search and Sort UI Components
const SearchSortContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 20px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 10px 12px;
  border: 1px solid #444;
  border-radius: 6px;
  font-size: 1rem;
  background-color: #1a1a1a;
  color: #ffffff;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #888;
  }
`;

const SortBubbles = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SortBubble = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: 1px solid #666;
  border-radius: 20px;
  background-color: ${props => props.$active ? '#4CAF50' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#cccccc'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    border-color: #4CAF50;
    color: white;
  }
`;

const InfiniteScrollSentinel = styled.div`
  height: 20px;
  margin: 20px 0;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #999;
  font-size: 1rem;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1.1rem;
  padding: 60px 20px;
`;




const Profile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { username } = useParams<{ username: string }>();
  const activeTab = searchParams.get('tab') || 'about';
  
  // Profile user state
  const [profileUser, setProfileUser] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  
  // Function to fetch profile user data
  const fetchProfileUser = useCallback(async () => {
    if (!username) return;
    
    setProfileLoading(true);
    setProfileError(null);
    
    try {
      const response = await fetch(`${config.apiUrl}/user/profile/${username}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch profile');
      }
      
      setProfileUser(data.user);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileError(error instanceof Error ? error.message : 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }, [username]);

  // Fetch profile user on component mount or username change
  useEffect(() => {
    fetchProfileUser();
  }, [fetchProfileUser]);


  // New meditation results state for My Meditations and Favorites
  const [myMeditations, setMyMeditations] = useState<MeditationResultItem[]>([]);
  const [favorites, setFavorites] = useState<MeditationResultItem[]>([]);
  const [myMeditationsLoading, setMyMeditationsLoading] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  
  // Search and sort state
  const [myMeditationsSearch, setMyMeditationsSearch] = useState('');
  const [favoritesSearch, setFavoritesSearch] = useState('');
  const [myMeditationsSort, setMyMeditationsSort] = useState('published_at_desc'); // default newest first
  const [favoritesSort, setFavoritesSort] = useState('created_at_desc'); // default most recent first
  
  // Debounce refs for search
  const myMeditationsSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const favoritesSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Refs to get current search values in timeouts
  const currentMyMeditationsSearch = useRef(myMeditationsSearch);
  const currentFavoritesSearch = useRef(favoritesSearch);
  
  // Update refs when state changes
  useEffect(() => {
    currentMyMeditationsSearch.current = myMeditationsSearch;
  }, [myMeditationsSearch]);
  
  useEffect(() => {
    currentFavoritesSearch.current = favoritesSearch;
  }, [favoritesSearch]);
  
  // Infinite scroll state for My Meditations
  const [myMeditationsHasMore, setMyMeditationsHasMore] = useState(true);
  const [myMeditationsOffset, setMyMeditationsOffset] = useState(0);
  const [myMeditationsTotalLoaded, setMyMeditationsTotalLoaded] = useState(0);
  
  // Infinite scroll state for Favorites
  const [favoritesHasMore, setFavoritesHasMore] = useState(true);
  const [favoritesOffset, setFavoritesOffset] = useState(0);
  const [favoritesTotalLoaded, setFavoritesTotalLoaded] = useState(0);
  
  const maxResults = 100;
  


  // Get profile image URL with fallback to default
  let profileImageUrl = '/images/profile/default256.png';
  let profileImage = profileUser?.profile_image;
  if (profileImage) {
    profileImageUrl = `https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/512/${profileImage}` 
  }

  const displayUserName = profileUser?.username || username;

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };



  // My Meditations API functions
  const buildMyMeditationsParams = (currentOffset: number, search?: string, sort?: string) => {
    const apiParams = new URLSearchParams();
    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');
    if (search && search.trim()) {
      apiParams.append('keyword', search.trim());
    }
    if (sort) {
      apiParams.append('sort', sort);
    }
    return apiParams;
  };

  const fetchMyMeditations = useCallback(async (reset = true) => {
    if (!profileUser?.id) return;
    
    setMyMeditationsLoading(true);
    
    try {
      const currentOffset = reset ? 0 : myMeditationsOffset;
      const apiParams = buildMyMeditationsParams(currentOffset, myMeditationsSearch, myMeditationsSort);
      
      const response = await fetch(`${config.apiUrl}/scripts/user/${profileUser.id}?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch meditations');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      if (reset) {
        setMyMeditations(meditations);
        setMyMeditationsOffset(meditations.length);
        setMyMeditationsTotalLoaded(meditations.length);
      } else {
        setMyMeditations(prev => [...prev, ...meditations]);
        setMyMeditationsOffset(prev => prev + meditations.length);
        setMyMeditationsTotalLoaded(prev => prev + meditations.length);
      }
      
      setMyMeditationsHasMore(meditations.length === 20 && (reset ? meditations.length : myMeditationsTotalLoaded + meditations.length) < maxResults);
    } catch (error) {
      console.error('Error fetching my meditations:', error);
      if (reset) {
        setMyMeditations([]);
      }
      setMyMeditationsHasMore(false);
    } finally {
      setMyMeditationsLoading(false);
    }
  }, [profileUser?.id, myMeditationsSearch, myMeditationsSort, myMeditationsOffset, myMeditationsTotalLoaded, maxResults]);

  const loadMoreMyMeditations = useCallback(async () => {
    if (myMeditationsLoading || !myMeditationsHasMore || myMeditationsTotalLoaded >= maxResults) return;
    fetchMyMeditations(false);
  }, [myMeditationsLoading, myMeditationsHasMore, myMeditationsTotalLoaded, maxResults, fetchMyMeditations]);

  // Favorites API functions
  const buildFavoritesParams = (currentOffset: number, search?: string, sort?: string) => {
    const apiParams = new URLSearchParams();
    apiParams.append('offset', currentOffset.toString());
    apiParams.append('limit', '20');
    if (search && search.trim()) {
      apiParams.append('keyword', search.trim());
    }
    if (sort) {
      apiParams.append('sort', sort);
    }
    return apiParams;
  };

  const fetchFavorites = useCallback(async (reset = true) => {
    if (!profileUser?.id) return;
    
    // Check if this user allows showing favorites
    const profileSettings = profileUser.profile_settings || {};
    if (profileSettings.dont_show_favorites) {
      setFavorites([]);
      return;
    }
    
    setFavoritesLoading(true);
    
    try {
      const currentOffset = reset ? 0 : favoritesOffset;
      const apiParams = buildFavoritesParams(currentOffset, favoritesSearch, favoritesSort);
      
      const response = await fetch(`${config.apiUrl}/scripts/user/${profileUser.id}/favorites?${apiParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken() || ''}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }

      const data = await response.json();
      const meditations = data.meditations || [];
      
      if (reset) {
        setFavorites(meditations);
        setFavoritesOffset(meditations.length);
        setFavoritesTotalLoaded(meditations.length);
      } else {
        setFavorites(prev => [...prev, ...meditations]);
        setFavoritesOffset(prev => prev + meditations.length);
        setFavoritesTotalLoaded(prev => prev + meditations.length);
      }
      
      setFavoritesHasMore(meditations.length === 20 && (reset ? meditations.length : favoritesTotalLoaded + meditations.length) < maxResults);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      if (reset) {
        setFavorites([]);
      }
      setFavoritesHasMore(false);
    } finally {
      setFavoritesLoading(false);
    }
  }, [profileUser?.id, favoritesSearch, favoritesSort, favoritesOffset, favoritesTotalLoaded, maxResults]);

  const loadMoreFavorites = useCallback(async () => {
    if (favoritesLoading || !favoritesHasMore || favoritesTotalLoaded >= maxResults) return;
    fetchFavorites(false);
  }, [favoritesLoading, favoritesHasMore, favoritesTotalLoaded, maxResults, fetchFavorites]);

  // Dedicated search function that uses current values
  const performMyMeditationsSearch = useCallback(() => {
    if (!profileUser?.id) return;
    
    setMyMeditationsLoading(true);
    setMyMeditations([]);
    setMyMeditationsOffset(0);
    setMyMeditationsTotalLoaded(0);
    setMyMeditationsHasMore(true);
    
    const apiParams = buildMyMeditationsParams(0, currentMyMeditationsSearch.current, myMeditationsSort);
    
    fetch(`${config.apiUrl}/scripts/user/${profileUser.id}?${apiParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${authService.getToken() || ''}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch meditations');
      }
      return response.json();
    })
    .then(data => {
      const meditations = data.meditations || [];
      setMyMeditations(meditations);
      setMyMeditationsOffset(meditations.length);
      setMyMeditationsTotalLoaded(meditations.length);
      setMyMeditationsHasMore(meditations.length === 20 && meditations.length < maxResults);
    })
    .catch(error => {
      console.error('Error fetching my meditations:', error);
      setMyMeditations([]);
      setMyMeditationsHasMore(false);
    })
    .finally(() => {
      setMyMeditationsLoading(false);
    });
  }, [profileUser?.id, myMeditationsSort, maxResults]);

  // Dedicated search function for favorites
  const performFavoritesSearch = useCallback(() => {
    if (!profileUser?.id) return;
    
    setFavoritesLoading(true);
    setFavorites([]);
    setFavoritesOffset(0);
    setFavoritesTotalLoaded(0);
    setFavoritesHasMore(true);
    
    const apiParams = buildFavoritesParams(0, currentFavoritesSearch.current, favoritesSort);
    
    fetch(`${config.apiUrl}/scripts/user/${profileUser.id}/favorites?${apiParams.toString()}`, {
      headers: {
        'Authorization': `Bearer ${authService.getToken() || ''}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }
      return response.json();
    })
    .then(data => {
      const meditations = data.meditations || [];
      setFavorites(meditations);
      setFavoritesOffset(meditations.length);
      setFavoritesTotalLoaded(meditations.length);
      setFavoritesHasMore(meditations.length === 20 && meditations.length < maxResults);
    })
    .catch(error => {
      console.error('Error fetching favorites:', error);
      setFavorites([]);
      setFavoritesHasMore(false);
    })
    .finally(() => {
      setFavoritesLoading(false);
    });
  }, [profileUser?.id, favoritesSort, maxResults]);

  // Search handlers
  const handleMyMeditationsSearch = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Clear timeout and search immediately on Enter
      if (myMeditationsSearchTimeout.current) {
        clearTimeout(myMeditationsSearchTimeout.current);
      }
      performMyMeditationsSearch();
    }
  }, [performMyMeditationsSearch]);

  const handleMyMeditationsSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setMyMeditationsSearch(newValue);
    
    // Clear existing timeout
    if (myMeditationsSearchTimeout.current) {
      clearTimeout(myMeditationsSearchTimeout.current);
    }
    
    // Set new timeout for debounced search
    myMeditationsSearchTimeout.current = setTimeout(() => {
      performMyMeditationsSearch();
    }, 300);
  }, [performMyMeditationsSearch]);

  const handleFavoritesSearch = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Clear timeout and search immediately on Enter
      if (favoritesSearchTimeout.current) {
        clearTimeout(favoritesSearchTimeout.current);
      }
      performFavoritesSearch();
    }
  }, [performFavoritesSearch]);

  const handleFavoritesSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setFavoritesSearch(newValue);
    
    // Clear existing timeout
    if (favoritesSearchTimeout.current) {
      clearTimeout(favoritesSearchTimeout.current);
    }
    
    // Set new timeout for debounced search
    favoritesSearchTimeout.current = setTimeout(() => {
      performFavoritesSearch();
    }, 300);
  }, [performFavoritesSearch]);

  // Sort handlers
  const handleMyMeditationsSort = useCallback((sort: string) => {
    setMyMeditationsSort(sort);
  }, []);

  const handleFavoritesSort = useCallback((sort: string) => {
    setFavoritesSort(sort);
  }, []);

  // Initial tab loading
  useEffect(() => {
    if (!profileUser?.id) return;
    
    if (activeTab === 'scripts') {
      fetchMyMeditations(true);
    } else if (activeTab === 'comments') {
      // TODO: Fetch comments
    } else if (activeTab === 'about') {
      // TODO: Fetch about
    } else if (activeTab === 'favorites') {
      fetchFavorites(true);
    }
  }, [activeTab, profileUser?.id, fetchMyMeditations, fetchFavorites]);

  // Effect to trigger refetch when sort changes (separate from search changes)
  useEffect(() => {
    if (activeTab === 'scripts' && myMeditationsSort) {
      fetchMyMeditations(true);
    }
  }, [myMeditationsSort]);

  useEffect(() => {
    if (activeTab === 'favorites' && favoritesSort) {
      fetchFavorites(true);
    }
  }, [favoritesSort]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (myMeditationsSearchTimeout.current) {
        clearTimeout(myMeditationsSearchTimeout.current);
      }
      if (favoritesSearchTimeout.current) {
        clearTimeout(favoritesSearchTimeout.current);
      }
    };
  }, []);

  // Set up infinite scroll for My Meditations
  const { sentinelRef: myMeditationsSentinelRef, loadingRef: myMeditationsLoadingRef } = useInfiniteScroll({
    loadMore: loadMoreMyMeditations,
    hasMore: myMeditationsHasMore && myMeditationsTotalLoaded < maxResults,
    loading: myMeditationsLoading
  });

  // Set up infinite scroll for Favorites
  const { sentinelRef: favoritesSentinelRef, loadingRef: favoritesLoadingRef } = useInfiniteScroll({
    loadMore: loadMoreFavorites,
    hasMore: favoritesHasMore && favoritesTotalLoaded < maxResults,
    loading: favoritesLoading
  });

 



  // Show loading state
  if (profileLoading) {
    return (
      <Container>
        <LoadingIndicator>Loading profile...</LoadingIndicator>
      </Container>
    );
  }

  // Show error state
  if (profileError) {
    return (
      <Container>
        <NoResultsMessage>{profileError}</NoResultsMessage>
      </Container>
    );
  }

  // Show not found if no profile user
  if (!profileUser) {
    return (
      <Container>
        <NoResultsMessage>User not found</NoResultsMessage>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title={`${displayUserName}'s Profile - YouMeditation`}
        description={`Discover meditations created by ${displayUserName}. View their published meditation scripts, favorites, and join the mindful community on YouMeditation.`}
        keywords={`${displayUserName}, meditation creator, meditation profile, YouMeditation user, mindfulness practitioner`}
        image={profileImageUrl}
        url={`/profile/${username}`}
        author={displayUserName}
        type="profile"
      />
      <Container>
      <Header>
        <Title>{displayUserName}'s Profile</Title>
        <ProfilePicture $imageUrl={profileImageUrl} />
      </Header>

      <Tabs>
        <Tab 
          $active={activeTab === 'about'} 
          onClick={() => handleTabChange('about')}
        >
          About
        </Tab>
        {!profileUser.profile_settings?.dont_show_scripts && (
          <Tab 
            $active={activeTab === 'scripts'} 
            onClick={() => handleTabChange('scripts')}
          >
            Meditations
          </Tab>
        )}
        {!profileUser.profile_settings?.dont_show_favorites && (
          <Tab 
            $active={activeTab === 'favorites'} 
            onClick={() => handleTabChange('favorites')}
          >
            Favorites
          </Tab>
        )}
        {!profileUser.profile_settings?.dont_show_comments && (
          <Tab 
            $active={activeTab === 'comments'} 
            onClick={() => handleTabChange('comments')}
          >
            Comments
          </Tab>
        )}
      </Tabs>

      {activeTab === 'scripts' && (
        <div>
          <SearchSortContainer>
            <SearchInput
              type="text"
              placeholder="Search meditations..."
              value={myMeditationsSearch}
              onChange={handleMyMeditationsSearchChange}
              onKeyDown={handleMyMeditationsSearch}
            />
            <SortBubbles>
              <SortBubble 
                $active={myMeditationsSort === 'published_at_desc'} 
                onClick={() => handleMyMeditationsSort('published_at_desc')}
              >
                New
              </SortBubble>
              <SortBubble 
                $active={myMeditationsSort === 'published_at_asc'} 
                onClick={() => handleMyMeditationsSort('published_at_asc')}
              >
                Old
              </SortBubble>
              <SortBubble 
                $active={myMeditationsSort === 'rating_desc'} 
                onClick={() => handleMyMeditationsSort('rating_desc')}
              >
                Popular
              </SortBubble>
            </SortBubbles>
          </SearchSortContainer>

          <MeditationResults 
            meditations={myMeditations} 
            loading={false}
          />

          {/* Infinite scroll loading indicator */}
          {myMeditationsLoading && myMeditations.length > 0 && (
            <LoadingIndicator ref={myMeditationsLoadingRef}>
              Loading more meditations...
            </LoadingIndicator>
          )}

          {/* Infinite scroll sentinel */}
          {myMeditationsHasMore && myMeditationsTotalLoaded < maxResults && !myMeditationsLoading && (
            <InfiniteScrollSentinel ref={myMeditationsSentinelRef} />
          )}

          {/* Show message when no results */}
          {!myMeditationsLoading && myMeditations.length === 0 && (
            <NoResultsMessage>
              {myMeditationsSearch.trim() ? 'No meditations found matching your search.' : 'You haven\'t created any published meditations yet.'}
            </NoResultsMessage>
          )}
        </div>
      )}

     

      {activeTab === 'favorites' && (
        <div>
          <SearchSortContainer>
            <SearchInput
              type="text"
              placeholder="Search favorites..."
              value={favoritesSearch}
              onChange={handleFavoritesSearchChange}
              onKeyDown={handleFavoritesSearch}
            />
            <SortBubbles>
              <SortBubble 
                $active={favoritesSort === 'created_at_desc'} 
                onClick={() => handleFavoritesSort('created_at_desc')}
              >
                Recent
              </SortBubble>
              <SortBubble 
                $active={favoritesSort === 'created_at_asc'} 
                onClick={() => handleFavoritesSort('created_at_asc')}
              >
                Old
              </SortBubble>
              <SortBubble 
                $active={favoritesSort === 'listen_count_desc'} 
                onClick={() => handleFavoritesSort('listen_count_desc')}
              >
                Most Listens
              </SortBubble>
              <SortBubble 
                $active={favoritesSort === 'listen_count_asc'} 
                onClick={() => handleFavoritesSort('listen_count_asc')}
              >
                Least Listens
              </SortBubble>
            </SortBubbles>
          </SearchSortContainer>

          <MeditationResults 
            meditations={favorites} 
            loading={false}
          />

          {/* Infinite scroll loading indicator */}
          {favoritesLoading && favorites.length > 0 && (
            <LoadingIndicator ref={favoritesLoadingRef}>
              Loading more favorites...
            </LoadingIndicator>
          )}

          {/* Infinite scroll sentinel */}
          {favoritesHasMore && favoritesTotalLoaded < maxResults && !favoritesLoading && (
            <InfiniteScrollSentinel ref={favoritesSentinelRef} />
          )}

          {/* Show message when no results */}
          {!favoritesLoading && favorites.length === 0 && (
            <NoResultsMessage>
              {favoritesSearch.trim() ? 'No favorites found matching your search.' : 'You haven\'t favorited any meditations yet.'}
            </NoResultsMessage>
          )}
        </div>
      )}

      {activeTab === 'about' && (
        <SettingsContainer>
          <SettingsSection>
            {profileUser.profile_settings?.about_me ? (
              <div style={{ color: '#cccccc', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {profileUser.profile_settings.about_me}
              </div>
            ) : (
              <div style={{ color: '#999', fontStyle: 'italic' }}>
                This user hasn't added an about section yet.
              </div>
            )}
          </SettingsSection>
        </SettingsContainer>
      )}

      {activeTab === 'comments' && (
        <Comments 
          targetType="profile" 
          targetId={profileUser.id} 
          targetOwnerId={profileUser.id}
        />
      )}

     
    </Container>
    </>
  );
};

export default Profile; 